import React, { FC } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Flex } from '@rebass/emotion';
import { gql, useQuery } from '@apollo/client';

import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import messages from './StepsProgress.messages';
import { StepState } from './StepsProgress.types';
import Step from './Step';
import { StepsProgressQuery } from './__generated__/StepsProgressQuery';

const ALL_STEPS = {
  address: {
    title: messages.address,
    route: `/:language/shop/checkout/address/`,
  },
  delivery: {
    title: messages.shipping,
    route: `/:language/shop/checkout/delivery/`,
  },
  review: {
    title: messages.review,
    route: `/:language/shop/checkout/review/`,
  },
  payment: {
    title: messages.payment,
    route: `/:language/shop/checkout/payment/:status?/`,
  },
};

const stepsVirtual = [ALL_STEPS.address, ALL_STEPS.review, ALL_STEPS.payment];

const stepsHardware = [
  ALL_STEPS.address,
  ALL_STEPS.delivery,
  ALL_STEPS.review,
  ALL_STEPS.payment,
];

const Steps: FC<{ isVirtual?: boolean }> = ({ isVirtual }) => {
  const { path } = useRouteMatch();
  const steps = isVirtual ? stepsVirtual : stepsHardware;

  return (
    <Flex p={3} alignItems="center" flex={1} maxWidth={rem(600)}>
      {steps.map((step, index) => {
        const stepIndex = steps.findIndex(({ route }) => route === path);
        const stepState: StepState = {
          step,
          stepNumber: index + 1,
          isActive: step.route === path,
          isPassed: stepIndex > index,
          isLast: steps.length - 1 === index,
        };

        return <Step key={step.route} stepState={stepState} />;
      })}
    </Flex>
  );
};

const StepsProgress: FC = () => {
  const { data, loading } = useQuery<StepsProgressQuery>(STEPS_PROGRESS_QUERY);

  if (loading) return null;

  const isVirtual = data?.magento2?.customerCart?.isVirtual;

  return (
    <Flex
      justifyContent="center"
      pb={rem(14)}
      bg={theme.colors.nearWhite}
      css={{ borderBottom: `${rem(2)} solid rgba(0, 0, 0, 0.1)` }}
    >
      <Steps isVirtual={isVirtual} />
    </Flex>
  );
};

export const STEPS_PROGRESS_QUERY = gql`
  query StepsProgressQuery {
    magento2 {
      customerCart {
        isVirtual
      }
    }
  }
`;

export default StepsProgress;
